import {OptibusClient} from "./optibus-client";
import {charterToPrivateHire} from "./busable2Optibus";
import dayjs from "dayjs";

class OptibusIntegration {
    constructor(operator, schedulesById = {}) {
        console.log('[OptibusIntegration] Initializing with operator:', operator);
        const {apiKey, accountName, baseUrl} = operator.opts?.external?.optibus || {};
        if (!apiKey || !accountName) {
            console.error('[OptibusIntegration] Missing required configuration:', { apiKey: !!apiKey, accountName: !!accountName });
            throw new Error('Missing required Optibus configuration (apiKey or accountName)');
        }

        this.client = new OptibusClient({
            apiKey,
            accountName,
            baseUrl: baseUrl || 'https://api.optibus.com'
        });
        this.operator = operator;
        this.schedulesById = schedulesById;
        console.log('[OptibusIntegration] Successfully initialized');
    }

    async upsertCharters(charters) {
        console.log('[upsertCharters] Starting with charters:', charters);
        const charterArray = Array.isArray(charters) ? charters : [charters];
        
        // Process all charters in parallel
        const results = await Promise.all(charterArray.map(async (charter) => {
            try {
                if (charter.status === 'cancelled') {
                    console.log(`[upsertCharters] Cancelling charter ${charter.bookingReference} in Optibus`);
                    const result = await this.client.updatePrivateHireOrderStatus(charter.bookingReference, {
                        orderStatus: 'cancelled'
                    });
                    console.log(`[upsertCharters] Successfully cancelled charter ${charter.bookingReference}`);
                    return { charter: charter.bookingReference, status: 'cancelled' };
                }

                console.log('[upsertCharters] Converting charter to private hire');
                const privateHire = charterToPrivateHire(charter, {                
                    depotName: this.operator?.opts?.external?.optibus?.depotName || 'Main Depot',
                    schedulesById: this.schedulesById,
                });
                console.log('[upsertCharters] Converted private hire:', privateHire);

                // Try to create first
                console.log(`[upsertCharters] Attempting to create charter ${charter.bookingReference} in Optibus`, privateHire);
                const result = await this.client.createPrivateHireOrder(privateHire);
                if(result?.errors?.length && result.errors.some(e => e.type === 'orderIdAlreadyExists')) {
                    console.log(`[upsertCharters] Charter ${charter.bookingReference} already exists, updating instead`);
                    await this.client.updatePrivateHireOrderTrips(charter.bookingReference, {
                        privateHires: privateHire.privateHires
                    });
                    console.log(`[upsertCharters] Successfully updated charter ${charter.bookingReference}`);
                    return { charter: charter.bookingReference, status: 'success' };
                }
                console.log(`[upsertCharters] Successfully created charter ${charter.bookingReference} in Optibus`);
                return { charter: charter.bookingReference, status: 'success' };
            } catch (error) {
                console.error(`[upsertCharters] Error processing charter ${charter.bookingReference}:`, error);
                return { charter: charter.bookingReference, status: 'failed', message: error.message };
            }
        }));

        return results;
    }

    async deleteCharters(charters) {
        console.log('[deleteCharters] Starting with charters:', charters);
        const charterArray = Array.isArray(charters) ? charters : [charters];
        
        // Process all charters in parallel
        const results = await Promise.all(charterArray.map(async (charter) => {
            try {
                // Check if charter exists in Optibus
                const exists = await this.checkCharterExists(charter.bookingReference);
                if (!exists) {
                    console.log(`[deleteCharters] Charter ${charter.bookingReference} does not exist in Optibus, skipping delete`);
                    return { charter: charter.bookingReference, status: 'skipped', reason: 'not_found' };
                }

                // First cancel the order
                console.log(`[deleteCharters] Cancelling charter ${charter.bookingReference}`);
                await this.client.updatePrivateHireOrderStatus(charter.bookingReference, {
                    orderStatus: 'cancelled'
                });
                
                // Then delete all associated trips
                console.log(`[deleteCharters] Deleting trips for charter ${charter.bookingReference}`);
                await this.client.updatePrivateHireOrderTrips(charter.bookingReference, {
                    privateHires: []
                });
                
                console.log(`[deleteCharters] Successfully deleted charter ${charter.bookingReference}`);
                return { charter: charter.bookingReference, status: 'success' };
            } catch (error) {
                console.error(`[deleteCharters] Error deleting charter ${charter.bookingReference}:`, error);
                return { charter: charter.bookingReference, status: 'failed', message: error.message };
            }
        }));

        return results;
    }

    async _getOperationalPlan({depotId = null, fromDate, toDate}) {
        console.log('[getOperationalPlan] Starting with params:', { depotId, fromDate, toDate });
        try {
            const depotUuid = depotId || this.operator.opts?.external?.optibus?.defaultDepotUuid;
            if (!depotUuid) {
                console.error('[getOperationalPlan] No depot UUID found');
                throw new Error('No depot UUID found for operational plan query');
            }

            const fromDateStr = dayjs(fromDate).format('YYYY-MM-DD');
            const toDateStr = dayjs(toDate).format('YYYY-MM-DD');

            console.log('[getOperationalPlan] Fetching plan with params:', {
                depotUuid,
                fromDate: fromDateStr,
                toDate: toDateStr
            });

            const operationalPlan = await this.client.getOperationalPlanV2({
                depotUuids: [depotUuid],
                fromDate: fromDateStr,
                toDate: toDateStr,
                includeUnassigned: true
            });

            console.log('[getOperationalPlan] Successfully fetched plan');
            return operationalPlan;
        } catch (error) {
            console.error('[getOperationalPlan] Error:', error);
            throw error;
        }
    }

    async getWorkItems({depotId = null, fromDate, toDate}) {
        console.log('[getWorkItems] Starting with params:', { depotId, fromDate, toDate });
        try {
            const operationalPlan = await this._getOperationalPlan({depotId, fromDate, toDate});
            console.log('[getWorkItems] Fetched operational plan');
            
            const vehicleEvents = operationalPlan.flatMap(depot => 
                depot.events.filter(event => event.vehicleUuid)
            );
            console.log('[getWorkItems] Found vehicle events:', vehicleEvents.length);

            return vehicleEvents;
        } catch (error) {
            console.error('[getWorkItems] Error:', error);
            throw error;
        }
    }
}

export const createOptibusIntegration = (operator, schedulesById = {}) => {
    console.log('[createOptibusIntegration] Creating new integration for operator:', operator);
    return new OptibusIntegration(operator, schedulesById);
}; 